import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

// Create the authentication context
const AuthContext = createContext();

const BACKEND_URL = '';

// Create an Axios instance
const API = axios.create({
  baseURL: BACKEND_URL,
});

API.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token'); // or however you store your token
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
);
// Hook to use the authentication context
export function useAuth() {
  return useContext(AuthContext);
}

// Provider component that wraps the app and makes auth available
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [token, setToken] = useState(localStorage.getItem('token') || '');

  // Configure axios to use the token in requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  }, [token]);

  // Check if the user is still logged in on page reload
  useEffect(() => {
    async function loadUserFromToken() {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await API.get('/api/users/profile');
        setCurrentUser(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading user from token:', error);
        // If token is invalid or expired, clear it
        setToken('');
        setCurrentUser(null);
        setLoading(false);
      }
    }

    loadUserFromToken();
  }, [token]);

  // Register a new user
  async function register(userData) {
    try {
      setError('');
      const response = await API.post('/api/auth/register', userData);
      setToken(response.data.token);
      setCurrentUser(response.data.user);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Registration failed');
      throw error;
    }
  }

  // Login a user
  async function login(username, password) {
    try {
      setError('');
      const response = await API.post('/api/auth/login', { username, password });
      setToken(response.data.token);
      setCurrentUser(response.data.user);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
      throw error;
    }
  }

  // Logout the user
  function logout() {
    setToken('');
    setCurrentUser(null);
  }

  // Update user profile
  async function updateProfile(profileData) {
    try {
      setError('');
      const response = await API.put('/api/users/profile', profileData);
      setCurrentUser(response.data.user);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Profile update failed');
      throw error;
    }
  }

  const value = {
    currentUser,
    loading,
    error,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}